import { cloneDeep } from "lodash";
import { Fragment, MarkType, NodeType, Node as ProsemirrorNode, ResolvedPos, Slice } from "prosemirror-model";
import { AllSelection, EditorState, NodeSelection, Selection, TextSelection, Transaction } from "prosemirror-state";
import { canSplit, ReplaceAroundStep } from "prosemirror-transform";
import { EditorView } from "prosemirror-view";
import invariant from "tiny-invariant";
import uuid4 from "uuid/v4";

import { BlockSelection } from "./blockselection";
import { LIST_ITEM_TYPE_NAMES, PLAIN_TEXT_NODE_TYPE_NAMES } from "./nodes/plaintext";
import { ASSIGNABLE_TYPE_NAMES } from "./plugins/assignable";
import { getBlockBoundaryState } from "./plugins/blockBoundary";
import {
	closestBlock,
	closestBlockDepth,
	entireRangeHasMark,
	expandSelectionToBlocks,
	getBlockPos,
	isBlock,
	isSelectionFromStart,
	isVisuallyEmpty,
} from "./utils";

export function expandRangeToIncludeDescendants(
	state: EditorState,
	$from: ResolvedPos,
	$to: ResolvedPos,
): [newStart: number, newEnd: number] {
	const { blockTree } = getBlockBoundaryState(state) ?? {};

	// If we don't have a block tree, we can't expand the selection to include descendants.
	if (!blockTree) {
		return [$from.pos, $to.pos];
	}

	let rangeStart, rangeEnd, endBlock;

	// The selection is around blocks instead of inside. This supports a
	// selection around a single leaf block like an embed as well.
	if ($to.depth === 0) {
		invariant(
			!!$to.nodeBefore && isBlock($to.nodeBefore),
			"Empty selection at start of doc, can't expand to include descendants",
		);
		// Not sure how likely it is for $from and $to to have different depths,
		// but it's not hard to support it. If $from isn't at depth 0 (i.e. between
		// blocks), it must be inside of a block, so we'll expand it to before that
		// block.
		rangeStart = $from.depth === 0 ? $from.pos : $from.before(1);
		rangeEnd = $to.pos;
		endBlock = $to.nodeBefore;
	} else {
		// We assume that the selection is _within_ the block, not around it.
		const toBlockDepth = closestBlockDepth($to);
		const fromBlockDepth = closestBlockDepth($from);

		rangeStart = $from.before(fromBlockDepth);
		rangeEnd = $to.after(toBlockDepth);
		endBlock = closestBlock($to);
	}

	let endBlockTreeNode = blockTree.getNodeByBlockKey(endBlock.attrs.blockId);

	while (endBlockTreeNode && endBlockTreeNode.block) {
		rangeEnd = endBlockTreeNode.start + endBlockTreeNode.block.nodeSize;

		const lastChildBlock = endBlockTreeNode.children[endBlockTreeNode.children.length - 1]?.block;
		if (!lastChildBlock) break;

		endBlockTreeNode = blockTree.getNodeByBlockKey(lastChildBlock.attrs.blockId);
	}

	return [rangeStart, rangeEnd];
}

export const adjustSelectionToIncludeDescendants = (state: EditorState, dispatch: (tr: Transaction) => void) => {
	const [newFrom, newTo] = expandRangeToIncludeDescendants(state, state.selection.$from, state.selection.$to);
	dispatch(state.tr.setSelection(BlockSelection.create(state.tr.doc, newFrom, newTo)));
};

export const getMoveSelection = (direction: "up" | "down") => {
	return (state: EditorState, dispatch?: (tr: Transaction) => void, view?: EditorView): boolean => {
		if (!view) return false;
		const selection = view.state.selection;
		const range = selection.$from.blockRange(selection.$to);
		const headFirst = selection.$head.pos < selection.$anchor.pos;

		if (!range) return false;

		let tr = state.tr;
		const [start, end] = expandSelectionToBlocks(selection.from, selection.to, state.doc);
		let target = -1;

		if (direction === "up") {
			target = start - (tr.doc.resolve(start).nodeBefore?.nodeSize ?? 0);
		} else if (direction === "down") {
			target = end + (tr.doc.resolve(end).nodeAfter?.nodeSize ?? 0);
		}

		if (target === -1) {
			return false;
		}

		if (!dispatch) {
			return true;
		}

		const slice = state.doc.slice(start, end);
		tr = tr.deleteRange(start, end);

		target = tr.mapping.map(target);
		tr = tr.replace(target, target, slice);

		const replaceMapping = tr.mapping.slice(tr.mapping.maps.length - 1);

		const targetHead = tr.doc.resolve(replaceMapping.map(target, headFirst ? -1 : 1));
		const targetAnchor = tr.doc.resolve(replaceMapping.map(target, headFirst ? 1 : -1));
		const newSelection = BlockSelection.create(tr.doc, targetAnchor.pos, targetHead.pos);

		tr = tr.setSelection(newSelection);
		dispatch(tr.scrollIntoView());

		return true;
	};
};

export const setBlockType = (
	nodeType: NodeType,
	attrs?: { [key: string]: unknown | null },
	selection: Selection | null = null,
) => {
	// selection determines the position of this operation
	// is not given, state.selection is used
	let newAttrs = attrs ? attrs : {};
	if (!Object.isExtensible(newAttrs)) {
		newAttrs = { ...newAttrs };
	}
	if (!("blockType" in newAttrs)) {
		newAttrs["blockType"] = nodeType.name;
	}
	function canChangeType(doc: ProsemirrorNode, pos: number, type: NodeType) {
		const $pos = doc.resolve(pos);
		const index = $pos.index();
		return $pos.parent.canReplaceWith(index, index + 1, type);
	}
	return function (state: EditorState, dispatch?: (tr: Transaction) => void) {
		const { from, to } = selection || state.selection;
		let applicable = false;
		state.doc.nodesBetween(from, to, (node, pos) => {
			if (applicable) return false;
			if (!node.isTextblock || node.hasMarkup(nodeType, { ...node.attrs, ...newAttrs })) return;
			if (node.type == nodeType) {
				applicable = true;
			} else {
				applicable = canChangeType(state.doc, pos, nodeType);
			}
			return;
		});
		if (!applicable) return false;
		if (dispatch) {
			const tr = state.tr;
			tr.doc.nodesBetween(from, to, (node: ProsemirrorNode, pos: number) => {
				const attrsWithId = { ...node.attrs, ...newAttrs };
				if (
					node.isTextblock &&
					!node.hasMarkup(nodeType, attrsWithId) &&
					canChangeType(tr.doc, tr.mapping.map(pos), nodeType)
				) {
					tr.clearIncompatible(tr.mapping.map(pos, 1), nodeType);
					const mapping = tr.mapping;
					const startM = mapping.map(pos, 1),
						endM = mapping.map(pos + node.nodeSize, 1);
					const extraAttrs: { [key: string]: unknown } = {};
					// If moving from a multiple assignee todo
					// Remove the assignees
					if (
						nodeType.name !== "todo" &&
						node.type.name === "todo" &&
						node.attrs.assignees &&
						node.attrs.assignees.length > 1
					) {
						extraAttrs.assignees = [];
					}
					if (!node.attrs.indent && LIST_ITEM_TYPE_NAMES.includes(nodeType.name)) {
						extraAttrs.indent = 1;
					} else if (
						node.attrs.indent &&
						LIST_ITEM_TYPE_NAMES.includes(node.type.name) &&
						ASSIGNABLE_TYPE_NAMES.includes(nodeType.name) &&
						"indent" in (nodeType.spec.attrs || {})
					) {
						extraAttrs.indent = node.attrs.indent - 1;
					}
					tr.step(
						new ReplaceAroundStep(
							startM,
							endM,
							startM + 1,
							endM - 1,
							new Slice(
								Fragment.from(nodeType.create({ ...attrsWithId, ...extraAttrs }, Fragment.empty, node.marks)),
								0,
								0,
							),
							1,
							true,
						),
					);
					return false;
				}
				return;
			});
			dispatch(tr);
		}
		return true;
	};
};

export const removeEmptyBlockBefore =
	(s: Selection | null = null) =>
	(state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const selection = s || state.selection;
		const { $from, empty } = selection;
		if (!empty || !isSelectionFromStart(selection)) return false;
		const { node, offset } = state.doc.childBefore($from.pos - $from.parentOffset - 1);
		if (!node || !isVisuallyEmpty(node)) return false;
		// TODO: find a better way instead of hard coding it here
		if (selection.$from.node(1).type.name === "image_strip") return false;
		if (dispatch) {
			let tr = state.tr;
			tr = tr.deleteRange(offset, offset + 2);
			dispatch(tr);
		}
		return true;
	};

export const mergeToPreviousEmptyBlock =
	(s: Selection | null = null) =>
	(state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const selection = s || state.selection;
		const { doc } = state;
		if (!selection.empty) return false;
		if (!isSelectionFromStart(selection)) return false;
		const [startOfCurrentBlock, endOfCurrentBlock] = expandSelectionToBlocks(selection.from, selection.to, doc);
		if (startOfCurrentBlock === selection.from || startOfCurrentBlock == 0) return false;
		const prevBlock = doc.childBefore(startOfCurrentBlock).node;
		if (!prevBlock || !prevBlock.isBlock || prevBlock.textContent) return false;

		// If it's an embed or a horizontal rule, select it instead of deleting it
		if (prevBlock.type.name === "embed" || prevBlock.type.name === "horizontal_rule") {
			// Default behaviour on an empty line is to select the embed
			if (isVisuallyEmpty(selection.$from.parent)) return false;

			if (dispatch) {
				let tr = state.tr;
				tr = tr.setSelection(NodeSelection.create(tr.doc, startOfCurrentBlock - 1));
				dispatch(tr);
			}
			return true;
		}

		// TODO: find a better way instead of hard coding it here
		if (selection.$from.node(1).type.name === "image_strip") return false;
		if (dispatch) {
			const text = selection.$from.parent.content;
			const tr = state.tr;
			tr.insert(startOfCurrentBlock - 1, text);
			tr.deleteRange(tr.mapping.map(startOfCurrentBlock), tr.mapping.map(endOfCurrentBlock));
			tr.setSelection(TextSelection.create(tr.doc, startOfCurrentBlock - 1));
			dispatch(tr);
		}
		return true;
	};

/**
 * Tweaked version of prosemirror-command's splitBlock command that
 * 	1. Sets different blockId after splitting
 * 	2. Splits header-one into header-one and unstyled (by allowing another handler to kick in)
 * 	3. Prevent ai-related attributes from being duplicated by the new block
 * Original version: https://github.com/ProseMirror/prosemirror-commands/blob/master/src/commands.ts
 */
export function splitBlock(state: EditorState, dispatch?: (tr: Transaction) => void) {
	let { $from, $to, empty } = state.selection;
	if (!empty) return false;
	if ($from.depth == 0) return false;
	if (!$from.parent.isBlock) return false;
	const atEnd = $to.parentOffset == $to.parent.content.size,
		atStart = $to.parentOffset === 0;
	if (atStart || atEnd) return false;

	const blockDepth = closestBlockDepth($from);
	const currentBlock = $from.node(blockDepth);
	let currentType = currentBlock.type;

	// See the Enter key handler in lib/prose/nodes/header.tsx
	if (currentType === state.schema.nodes["header-one"]) {
		return false;
	}

	if (dispatch) {
		let tr = state.tr;
		if (state.selection instanceof TextSelection || state.selection instanceof AllSelection) tr.deleteSelection();
		let types: { type: NodeType; attrs?: { [key: string]: unknown } | null | undefined }[] | undefined = [
			{ type: currentType },
		];
		let can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
		if (can) {
			const typesWithAttrs = types?.map(({ type }) => {
				let attrs: { [key: string]: unknown } = cloneDeep(currentBlock.attrs);
				if (type.spec.attrs && "blockId" in type.spec.attrs) {
					attrs["blockId"] = uuid4();
				}

				// At the time of writing there are no situations when splitting a block where we want to have these attributes.
				delete attrs.recordingOffset;
				delete attrs.recording;
				delete attrs.aiGenerated;

				return {
					type,
					attrs,
				};
			});
			tr.split(tr.mapping.map($from.pos), 1, typesWithAttrs);
			if (!$from.parentOffset && $from.parent.type != currentType) {
				let first = tr.mapping.map($from.before()),
					$first = tr.doc.resolve(first);
				if (currentType && $from.node(-1).canReplaceWith($first.index(), $first.index() + 1, currentType))
					tr.setNodeMarkup(tr.mapping.map($from.before()), currentType);
			}
		}
		dispatch(tr.scrollIntoView());
	}
	return true;
}

export const setUnstyled = (dedentPlainText: boolean) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
	const { selection } = state;
	if (!selection.empty || !isSelectionFromStart(selection)) return false;
	const parent = selection.$from.parent;
	if (!isVisuallyEmpty(parent, { ignoreText: true })) {
		let attrs = parent.attrs;
		if (
			dedentPlainText &&
			PLAIN_TEXT_NODE_TYPE_NAMES.includes(parent.type.name) &&
			parent.type.name !== "unstyled" &&
			attrs.indent > 0
		) {
			attrs = { ...attrs, indent: parent.attrs.indent - 1 };
		}
		return setBlockType(state.schema.nodes.unstyled, attrs)(state, dispatch);
	}
	return false;
};

export const setEmptyBlockToUnstyled =
	(dedentPlainText = false) =>
	(state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const { selection } = state;
		if (!selection.empty) return false;
		if (!isSelectionFromStart(selection)) return false;
		const parent = selection.$from.parent;
		if (parent.textBetween(0, parent.content.size, undefined, "\n")) return false;
		if (parent.type.name === "unstyled") return false;
		let attrs = selection.$from.parent.attrs;
		if (
			dedentPlainText &&
			PLAIN_TEXT_NODE_TYPE_NAMES.includes(parent.type.name) &&
			parent.type.name !== "unstyled" &&
			attrs.indent > 0
		) {
			attrs = { ...attrs, indent: parent.attrs.indent - 1 };
		}
		return setBlockType(state.schema.nodes.unstyled, attrs)(state, dispatch);
	};

// Handles Enter at start of line
export const createSameTypeBefore = (state: EditorState, dispatch?: (tr: Transaction) => void) => {
	const { $from, empty } = state.selection;
	if (!empty) return false;
	if (!isSelectionFromStart(state.selection)) return false;
	if ($from.node().type.name.startsWith("header")) {
		return false;
	}
	const initialAttrs = (() => {
		const indent = $from.node().attrs.indent;
		if (indent !== undefined) {
			return { indent };
		}
		return {};
	})();
	// create blockId here so we don't need additional transaction to fix it
	const newNode = $from.node().type.createAndFill({ ...initialAttrs, blockId: uuid4() });
	if (!newNode) return false;
	const startOfCurrentBlock = getBlockPos($from.pos, state.doc);
	if (startOfCurrentBlock === null) return false;
	if ($from.node(-1).type.name !== "doc") return false; // only do this in doc's direct children
	if (dispatch) {
		const tr = state.tr;
		tr.insert(startOfCurrentBlock, newNode);
		tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map(startOfCurrentBlock + 1)));
		tr.scrollIntoView();
		dispatch(tr);
	}
	return true;
};

// Handles Enter at end of line
export const createSameTypeAfter =
	(ignoreTypes: string[]) => (state: EditorState, dispatch?: (tr: Transaction) => void) => {
		const { $from, empty, $to } = state.selection;
		if (!empty) return false;
		if ($from.nodeAfter) return false;
		const initialAttrs = (() => {
			const indent = $from.node().attrs.indent;
			if (indent !== undefined) {
				return { indent };
			}
			return {};
		})();
		if (ignoreTypes.some(typeName => $from.node().type.name.startsWith(typeName))) {
			return false;
		}
		const newNode = $from.node().type.createAndFill({ ...initialAttrs, blockId: uuid4() });
		if (!newNode) return false;
		if (!$from.node().canAppend(newNode)) return false;
		if ($from.node(-1).type.name !== "doc") return false; // only do this in doc's direct children
		if (dispatch) {
			const tr = state.tr;
			const side = (!$from.parentOffset && $to.index() < $to.parent.childCount ? $from : $to).pos;
			tr.insert($from.pos + 1, newNode);
			tr.setSelection(TextSelection.create(tr.doc, side + 2));
			tr.scrollIntoView();
			dispatch(tr);
		}
		return true;
	};

export const escapeMark =
	(markType: MarkType) =>
	(state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
		const {
			selection: { empty, $from },
		} = state;
		if (!empty || !$from.nodeBefore) return false;
		if (
			$from.marks().some(m => m.type === markType) &&
			state.storedMarks &&
			!state.storedMarks.some(m => m.type === markType)
		)
			return false;
		const markBefore = $from.nodeBefore.marks.some(mark => mark.type === markType);
		const markAfter = $from.nodeAfter && $from.nodeAfter.marks.some(mark => mark.type === markType);
		if (!markBefore || markAfter) return false;
		if (dispatch) {
			const tr = state.tr;
			tr.removeStoredMark(markType);
			dispatch(tr);
		}
		return true;
	};

/**
 * Custom version of toggleMark. In this case, only remove the mark if it
 * exists across the entirety of the selection, otherwise add it to the whole
 * selection.
 */
export function toggleMark($type: MarkType | null, attrs: { [k: string]: unknown }) {
	if (!$type) {
		throw Error(`Cannot call 'toggleMark' with an invalid mark type. Received '$type' as '${$type}'.`);
	}
	return function doToggleMark(state: EditorState, dispatch?: (tr: Transaction) => void) {
		let { tr } = state;
		const { from, to, empty } = state.selection;
		const $cursor = state.selection instanceof TextSelection ? state.selection.$cursor : undefined;
		const mark = $type.create(attrs);

		if (empty) {
			const storedMarks = tr.storedMarks ?? $cursor?.marks();
			if (storedMarks && mark.isInSet(storedMarks)) {
				tr = tr.removeStoredMark(mark);
			} else {
				tr = tr.addStoredMark(mark);
			}
		} else if (entireRangeHasMark(from, to, mark)(state)) {
			tr = tr.removeMark(from, to, mark);
		} else {
			tr = tr.addMark(from, to, mark);
		}
		dispatch?.(tr.scrollIntoView());
		return true;
	};
}

/**
 * Creates an empty block above or below the cursor if at the top or bottom of the note
 */
export const makeSpace =
	(direction: "below" | "above") =>
	(state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
		const { selection } = state;

		// Only work for embeds
		const embedNode = selection.$from.nodeAfter;
		if (embedNode?.type.name !== "embed") return false;

		const newBlock = state.schema.nodes.unstyled.createAndFill({ blockId: uuid4() });

		if (!newBlock) {
			console.error("Failed to create a new block");
			return false;
		}

		const tr = state.tr;
		const targetChild = direction === "above" ? tr.doc.firstChild : tr.doc.lastChild;
		if (!targetChild) return false;

		if (embedNode.eq(targetChild)) {
			const targetPosition = direction === "above" ? selection.from : selection.from + 1;

			if (dispatch) {
				tr.insert(targetPosition, newBlock);
				tr.setSelection(TextSelection.create(tr.doc, selection.from + 1));
				tr.scrollIntoView();
				dispatch(tr);
			}
			return true;
		}

		return false;
	};

// non-commands
export const removeBlockById = (blockId: string, tr: Transaction) => {
	const doc = tr.doc;
	let block = null;
	let pos = 0;
	for (let index = 0; index < doc.childCount; index++) {
		const child = doc.child(index);
		if (child.attrs.blockId === blockId) {
			block = child;
			break;
		} else {
			pos += child.nodeSize;
		}
	}
	if (!block) return tr;
	return tr.deleteRange(pos, pos + block.nodeSize);
};
